/* eslint-disable eqeqeq */
import * as React from 'react';
import { GroupSetCustom, GroupSetProps } from './index';
import { useField } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import { ReactDatePickerProps } from 'react-datepicker';

import { FormViewContext } from '../context/FormViewContext';
import TimePicker, { TimePickerValue } from 'react-time-picker';
import { format } from 'date-fns';
import { parseCalendarDate } from '../../dateUtils';

function normalizeDate(val: unknown, calendarOnly: boolean): Date | null {
    if (val == null || val === '') return null;

    if (calendarOnly) {
        return parseCalendarDate(val);
    }

    if (val instanceof Date && !isNaN(val.getTime())) return val;
    if (typeof val === 'string') {
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d;
    }
    return null;
}

type DateParts = { y: number; m: number; d: number };

const emptyParts = (): DateParts => ({ y: 0, m: 0, d: 0 });

function partsFromDate(d: Date | null): DateParts {
    if (!d) return emptyParts();
    const dim = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return {
        y: d.getFullYear(),
        m: d.getMonth() + 1,
        d: Math.min(d.getDate(), dim),
    };
}

function monthLabel(month1To12: number): string {
    return new Date(2020, month1To12 - 1, 1).toLocaleString('en-CA', { month: 'long' });
}

declare type GroupSetDatePickerProps = GroupSetProps & {
    date: Date | null;
    pickerConfig?: Partial<ReactDatePickerProps>;
    onValueChanged?: () => void;
    noTime?: boolean;
    showYearPicker?: boolean;
};

export const GroupSetDatePicker = (props: GroupSetDatePickerProps) => {
    const [field, , helpers] = useField(props.fieldName);
    const formViewContext = useContext(FormViewContext);
    const calendarOnly = props.noTime !== false;

    const externalDate = useMemo(
        () => normalizeDate(props.date ?? field.value, calendarOnly),
        [props.date, field.value, calendarOnly]
    );

    const externalKey = externalDate ? String(externalDate.getTime()) : 'empty';

    const [pickerDate, setPickerDate] = useState<Date | null>(() => normalizeDate(props.date ?? field.value, calendarOnly));
    const [timeDate, setTimeDate] = useState<Date | null>(() => normalizeDate(props.date ?? field.value, calendarOnly));
    const [parts, setParts] = useState<DateParts>(() => partsFromDate(normalizeDate(props.date ?? field.value, calendarOnly)));

    const disabled = props.disabled || formViewContext.readonly;

    // Sync from Formik / props only when the normalized instant changes (avoids fighting local edits on unrelated re-renders).
    useEffect(() => {
        setPickerDate(externalDate);
        setTimeDate(externalDate);
        setParts(partsFromDate(externalDate));
    }, [externalKey]);

    const onTimeChangeHandler = (x: TimePickerValue) => {
        const base = pickerDate ?? normalizeDate(field.value, calendarOnly);
        if (!base) return;

        const hours = parseInt((x?.toString() || '00:00').substr(0, 2), 10);
        const minutes = parseInt((x?.toString() || '00:00').substr(3), 10);

        const currentDate = new Date(base.getTime());
        currentDate.setHours(hours, minutes, 0, 0);

        setTimeDate(currentDate);
        setPickerDate(currentDate);
        helpers.setValue(currentDate);
    };

    const getTimeValue = (): string => {
        if (pickerDate == undefined || pickerDate == null) return '00:00';
        return format(pickerDate, 'HH:mm');
    };

    const updatePart = (key: keyof DateParts, raw: number) => {
        if (disabled) return;
        const v = Number(raw);
        let nextY = key === 'y' ? v : parts.y;
        let nextM = key === 'm' ? v : parts.m;
        let nextD = key === 'd' ? v : parts.d;

        if (nextY && nextM && nextD) {
            const dim = new Date(nextY, nextM, 0).getDate();
            if (nextD > dim) nextD = dim;
        }

        const nextParts: DateParts = { y: nextY, m: nextM, d: nextD };
        setParts(nextParts);

        if (nextParts.y && nextParts.m && nextParts.d) {
            const prev = pickerDate ?? externalDate ?? new Date();
            const useTime = props.noTime === false;
            const h = useTime ? prev.getHours() : 0;
            const mi = useTime ? prev.getMinutes() : 0;
            const s = useTime ? prev.getSeconds() : 0;
            const nextDate = new Date(nextParts.y, nextParts.m - 1, nextParts.d, h, mi, s, 0);
            setPickerDate(nextDate);
            setTimeDate(nextDate);
            helpers.setValue(nextDate);
        } else {
            setPickerDate(null);
            setTimeDate(null);
            helpers.setValue(null);
        }
    };

    useEffect(() => {
        if (props.onValueChanged) props.onValueChanged();
    }, [pickerDate, props.onValueChanged]);

    const months: number[] = [];
    for (let i = 1; i <= 12; i++) {
        months.push(i);
    }

    const days: number[] = [];
    for (let i = 1; i <= 31; i++) {
        days.push(i);
    }

    const years: number[] = [];
    for (let i = 1950; i <= new Date().getFullYear() + 1; i++) {
        years.push(i);
    }

    const fieldKey = String(props.fieldName).replace(/\./g, '-');

    return (
        <GroupSetCustom {...props}>
            <div className={'form-control date-picker ' + (!props.noTime ? ' with-time ' : '')}>
                <select
                    name={`${fieldKey}-month`}
                    className="form-control month-dropdown"
                    disabled={disabled}
                    value={parts.m}
                    onChange={(e) => updatePart('m', parseInt(e.target.value, 10))}
                >
                    <option value={0}>Month</option>
                    {months.map((month) => (
                        <option key={month} value={month}>
                            {monthLabel(month)}
                        </option>
                    ))}
                </select>

                <select
                    name={`${fieldKey}-day`}
                    className="form-control day-dropdown"
                    disabled={disabled}
                    value={parts.d}
                    onChange={(e) => updatePart('d', parseInt(e.target.value, 10))}
                >
                    <option value={0}>Day</option>
                    {days.map((day) => (
                        <option key={day} value={day}>
                            {day}
                        </option>
                    ))}
                </select>

                <select
                    name={`${fieldKey}-year`}
                    className="form-control year-dropdown"
                    disabled={disabled}
                    value={parts.y}
                    onChange={(e) => updatePart('y', parseInt(e.target.value, 10))}
                >
                    <option value={0}>Year</option>
                    {years.map((year) => (
                        <option key={year} value={year}>
                            {year}
                        </option>
                    ))}
                </select>

                {props.noTime !== true && (
                    <TimePicker className="ms-3" onChange={onTimeChangeHandler} value={getTimeValue()} />
                )}
            </div>
        </GroupSetCustom>
    );
};
